"use client"

import clsx from "clsx"
import {
  ComponentProps,
  KeyboardEvent,
  Ref,
  SyntheticEvent,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from "react"
import { NpErrorMessage } from "src/components/np-design/components/NpErrorMessage"
import { NpInputLabel } from "src/components/np-design/components/input/NpInputLabel"
import { useEventCallback } from "src/hooks/useEventCallback"
import { useExperimentVariant } from "src/integrations/posthog/useExperiment"
import { useErrorMessageId } from "src/utilities/errorScrolling"

export type NpInputProps = {
  ref?: Ref<HTMLInputElement>
  placeholder: string
  autoComplete: string
  type?: ComponentProps<"input">["type"]
  color?: "green" | "gray"
  required?: boolean
  disabled?: boolean
  multiline?: boolean
  minLines?: number
  maxLength?: number
  autoFocus?: boolean
  value: string | undefined
  error?: string
  showError?: boolean
  className?: string
  onKeyDown?: (event: KeyboardEvent) => void
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
}

export const NpInput = ({
  ref: propRef,
  placeholder,
  autoComplete,
  type = "text",
  color = "green",
  required,
  disabled,
  multiline,
  minLines = 3,
  maxLength,
  autoFocus,
  value,
  error,
  showError,
  className,
  onKeyDown,
  onChange,
  onFocus,
  onBlur,
}: NpInputProps) => {
  const id = useId()
  const errorId = useErrorMessageId()
  const localRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(propRef, () => localRef.current!)

  // Adding a new line briefly creates a scrollbar which results in visual
  // jitter. This can be avoided by using a layout effect.
  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    const textarea = localRef.current
    if (!multiline || !textarea) return

    // Reset height to auto to get the correct scrollHeight.
    textarea.style.minHeight = "auto"
    textarea.style.height = "auto"

    const height = textarea.scrollHeight

    textarea.style.minHeight = height + "px"
    textarea.style.height = height + "px"
    textarea.scrollTop = height
  }, [multiline, minLines, value])

  useEffect(() => {
    if (autoFocus && localRef.current) {
      localRef.current.focus()
    }
  }, [autoFocus])

  const handleChange = useEventCallback((event: SyntheticEvent) => {
    const value = (event.currentTarget as HTMLInputElement).value
    const boundedValue = maxLength ? value.slice(0, maxLength) : value

    onChange(boundedValue)
  })

  const Element = multiline ? "textarea" : "input"
  const hasError = !!error && showError

  const experiment = useExperimentVariant("no-required-address-fields")

  return (
    <div
      id={hasError ? errorId : undefined}
      className="group text-default relative flex flex-col gap-1 text-base"
      inert={disabled}
    >
      <NpInputLabel htmlFor={id} elevated={!!value} hasError={hasError}>
        {placeholder +
          (required && experiment.variant === "control" ? " *" : "") +
          (!required && type !== "search" && experiment.variant === "test" ? " (optional)" : "")}
      </NpInputLabel>

      <Element
        ref={localRef as any}
        id={id}
        name={autoComplete}
        autoComplete={autoComplete}
        type={type}
        aria-placeholder={placeholder}
        required={required}
        disabled={disabled}
        rows={minLines}
        value={value || ""}
        onKeyDown={onKeyDown}
        onChange={handleChange}
        onPaste={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={clsx(className, "h-12 rounded-3xl border px-4 py-3 placeholder-transparent", {
          "border-np-green25": !hasError && color === "green",
          "border-np-gray25": !hasError && color === "gray",

          "hover:border-np-green focus:border-np-green": !hasError && !disabled && color === "green",
          "hover:border-np-gray75 focus:border-np-gray75": !hasError && !disabled && color === "gray",

          "border-np-error!": hasError,
          "resize-none overflow-hidden": multiline,
        })}
      />

      {(maxLength || hasError) && (
        <div className="flex">
          {maxLength && (
            <div className="mr-2 ml-auto text-sm">
              {value?.length || 0}/{maxLength}
            </div>
          )}

          {hasError && (
            <div className="px-4">
              <NpErrorMessage message={error} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
